import { useTranslation } from 'react-i18next'
import { clsx } from 'clsx'
import { MissingRateError, crossRate, rateFractionDigits, type Decimal } from '@globalx/core'
import { CurrencyFlag } from '@/components/currency/CurrencyFlag'
import { DemoBadge, SectionHeader, Skeleton } from '@/components/ui/primitives'
import { useFormat } from '@/lib/format'
import { useConverter, type Pair } from '@/store/converter'
import { useRates } from './useRates'

const DEFAULT_PAIRS: Pair[] = [
  { from: 'USD', to: 'EUR' },
  { from: 'USD', to: 'SAR' },
  { from: 'EUR', to: 'USD' },
  { from: 'USD', to: 'TRY' },
  { from: 'GBP', to: 'USD' },
  { from: 'USD', to: 'AED' },
  { from: 'USD', to: 'YER' },
  { from: 'USD', to: 'EGP' },
]

export function QuickConversions({ className }: { className?: string }) {
  const { t } = useTranslation()
  const { fixed } = useFormat()
  const { snapshot } = useRates()
  const rateFor = (p: Pair): Decimal | null => {
    if (!snapshot) return null
    try {
      return crossRate(snapshot, p.from, p.to)
    } catch (e) {
      if (e instanceof MissingRateError) return null
      throw e
    }
  }
  const { from, to, setPair, savedPairs } = useConverter()

  const seen = new Set<string>()
  const pairs = [...savedPairs, ...DEFAULT_PAIRS].filter((p) => {
    const k = `${p.from}-${p.to}`
    if (seen.has(k)) return false
    seen.add(k)
    return true
  })

  return (
    <section className={className} aria-labelledby="quick-title">
      <SectionHeader className="mb-3" title={<span id="quick-title">{t('home.quick')}</span>} badge={snapshot?.kind === 'demo' ? <DemoBadge /> : undefined} />
      <div className="scrollbar-none -mx-4 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:grid md:grid-cols-4 md:px-0">
        {pairs.slice(0, 8).map((p) => {
          const rate = rateFor(p)
          const active = p.from === from && p.to === to
          return (
            <button
              key={`${p.from}-${p.to}`}
              type="button"
              onClick={() => setPair(p)}
              aria-pressed={active}
              className={clsx(
                'card flex min-w-[9.5rem] shrink-0 flex-col gap-2 p-3 text-start transition hover:border-line-strong',
                active && 'border-gold-400/50! bg-gold-400/8',
              )}
            >
              <span className="ltr flex items-center gap-1.5 self-start">
                <span className="flex -space-x-1.5">
                  <CurrencyFlag code={p.from} className="size-5" />
                  <CurrencyFlag code={p.to} className="size-5" />
                </span>
                <span className="text-xs font-semibold">
                  {p.from} → {p.to}
                </span>
              </span>
              {rate ? (
                <span className="num ltr self-start text-sm font-semibold">{fixed(rate, rateFractionDigits(rate))}</span>
              ) : (
                <Skeleton className="h-5 w-16" />
              )}
            </button>
          )
        })}
      </div>
    </section>
  )
}
